import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { closeSnackbar } from 'notistack';

import { StyledSnackBarButton, StyledSnackBarContent } from '../styles';
import { INITIAL_BUTTON_COLOR } from '../../constants';
import { getColorHex } from '../../helpers';

import type { IDynamicSnackBarProps } from './types';

export const DynamicSnackBar = React.forwardRef<HTMLDivElement, IDynamicSnackBarProps>((props, ref) => {
  const {
    id,
    message: initialMessage,
    buttonText: initialButtonText,
    customAction,
    dataTestId,
    color: initialBackground,
    buttonTextColor: initialButtonTextColor,
    icon: initialIcon,
    update$,
  } = props;

  const [msg, setMsg] = useState(initialMessage);
  const [btnText, setBtnText] = useState(initialButtonText ?? 'Close');
  const [bg, setBg] = useState(getColorHex(initialBackground));
  const [btnColor, setBtnColor] = useState(initialButtonTextColor || INITIAL_BUTTON_COLOR);
  const [icon, setIcon] = useState<ReactNode>(initialIcon);

  useEffect(() => {
    if (!update$) return;

    const subscription = update$.subscribe((data) => {
      if (data.msg) setMsg(data.msg);
      if (data.color) setBg(getColorHex(data.color));
      if (data.buttonText) setBtnText(data.buttonText);
      if (data.buttonTextColor) setBtnColor(data.buttonTextColor);

      if (data.icon !== undefined) setIcon(data.icon);

      if (data.forceClose) {
        closeSnackbar(id);
      }
    });

    return () => subscription.unsubscribe();
  }, [update$, id]);

  const handleClick = () => {
    if (customAction) customAction();
    closeSnackbar(id);
  };

  return (
    <StyledSnackBarContent $backgroundColor={bg} ref={ref} data-testid={dataTestId ?? ''}>
      <Box display='flex'>
        {Boolean(icon) && (
          <Box sx={{ marginInlineEnd: '8px' }} component='span' display='flex' alignItems='center' color='inherit'>
            {icon}
          </Box>
        )}
        <Typography sx={{ padding: '0' }} variant='body2'>
          {msg}
        </Typography>
      </Box>

      <StyledSnackBarButton
        onClick={handleClick}
        variant='text'
        sx={{
          color: btnColor,
          minWidth: 'auto',
          marginLeft: 1,
        }}
      >
        {btnText}
      </StyledSnackBarButton>
    </StyledSnackBarContent>
  );
});

DynamicSnackBar.displayName = 'DynamicSnackBar';
